import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Loader2, MessageSquare, Send, Clock } from "lucide-react";
import {
  getConversa,
  listMensagens,
  enviarMensagem,
  marcarLido as apiMarcarLido,
} from "../../services/instagramApi";

const LIMIT = 40;

/* ──────────────── helpers de data ──────────────── */

function rotularDia(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(hoje.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, hoje)) return "HOJE";
  if (sameDay(d, ontem)) return "ONTEM";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}

function chaveDia(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function formatarHora(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// Cada mensagem pode ter timestamp em campos diferentes (recebido_em para in,
// enviado_em para out, data_criacao genérico). Resolve um só.
function timestampMsg(m) {
  return m?.data_criacao ?? m?.recebido_em ?? m?.enviado_em ?? null;
}

function extrairTexto(conteudo) {
  if (!conteudo) return "";
  if (typeof conteudo === "string") return conteudo;
  if (typeof conteudo.text === "string") return conteudo.text;
  if (conteudo.text && typeof conteudo.text.body === "string") return conteudo.text.body;
  if (typeof conteudo.body === "string") return conteudo.body;
  return "";
}

/* ──────────────── JanelaIndicator ──────────────── */

function JanelaIndicator({ janelaExpiraEm }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(i);
  }, []);

  const aberta = janelaExpiraEm && new Date(janelaExpiraEm).getTime() > Date.now();

  if (!aberta) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-4 bg-surface-2 border border-white/[0.08] rounded-full px-2.5 py-1">
        <span className="w-1.5 h-1.5 rounded-full bg-surface-4" />
        Janela fechada
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-300 bg-emerald-950 border border-emerald-800 rounded-full px-2.5 py-1"
      title={`Janela expira em ${new Date(janelaExpiraEm).toLocaleString("pt-BR")}`}
    >
      <Clock className="w-3 h-3" />
      Janela aberta
    </span>
  );
}

/* ──────────────── MediaAttachment (read-only) ──────────────── */

function MediaAttachment({ attachment }) {
  const tipo = (attachment?.type || "").toLowerCase();
  const url = attachment?.payload?.url || attachment?.url || null;

  if (!url) {
    return <p className="text-xs text-ink-4 italic">[mídia]</p>;
  }

  if (tipo === "image" || tipo === "story_mention" || tipo === "share") {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <img
          src={url}
          alt="Mídia recebida"
          className="rounded-lg max-w-full max-h-64 object-cover"
          loading="lazy"
        />
      </a>
    );
  }
  if (tipo === "video") {
    return (
      <video src={url} controls className="rounded-lg max-w-full max-h-64" preload="metadata" />
    );
  }
  if (tipo === "audio") {
    return <audio src={url} controls className="max-w-full" preload="metadata" />;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs text-brand-300 hover:text-brand-200 underline break-all"
    >
      Abrir mídia
    </a>
  );
}

/* ──────────────── MessageBubble ──────────────── */

function MessageBubble({ mensagem, eu }) {
  const { conteudo } = mensagem;
  const texto = extrairTexto(conteudo);
  const attachments = Array.isArray(conteudo?.attachments) ? conteudo.attachments : [];
  const ts = timestampMsg(mensagem);

  const alinhamento = eu ? "items-end" : "items-start";
  const corBolha = eu ? "bg-brand-900 border-brand-800" : "bg-surface-2 border-white/[0.08]";

  return (
    <div className={`flex flex-col ${alinhamento} my-0.5 px-3`}>
      <div className="relative max-w-[72%]">
        <div
          className={`relative rounded-xl border px-3 py-2 ${corBolha} shadow-sm`}
          role="article"
          aria-label={eu ? "Mensagem enviada" : "Mensagem recebida"}
        >
          {attachments.length > 0 && (
            <div className="flex flex-col gap-1.5 mb-1">
              {attachments.map((att, i) => (
                <MediaAttachment key={i} attachment={att} />
              ))}
            </div>
          )}

          {texto && (
            <p className="text-xs text-ink-1 whitespace-pre-wrap leading-relaxed break-words">
              {texto}
            </p>
          )}

          {!texto && attachments.length === 0 && (
            <p className="text-xs text-ink-4 italic">[mídia]</p>
          )}

          <div className={`flex items-center gap-1 mt-1 ${eu ? "justify-end" : "justify-start"}`}>
            <span className="text-[10px] text-ink-4 leading-none select-none">
              {formatarHora(ts)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── DiaSeparador ──────────────── */

function DiaSeparador({ iso }) {
  return (
    <div className="flex justify-center my-3 px-3">
      <span className="text-[11px] font-semibold tracking-wider uppercase text-ink-4 bg-surface-2/80 border border-white/[0.06] rounded-full px-3 py-1 select-none">
        {rotularDia(iso)}
      </span>
    </div>
  );
}

/* ──────────────── ChatInput (só texto) ──────────────── */

function ChatInput({ janelaAberta, enviando, onEnviar }) {
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState(null);
  const ref = useRef(null);

  const janelaFechada = janelaAberta === false;
  const podeEnviar = !janelaFechada && texto.trim().length > 0 && !enviando;

  async function handleEnviar() {
    if (!podeEnviar) return;
    const txt = texto.trim();
    setTexto("");
    setErro(null);
    ref.current?.focus();
    try {
      await onEnviar(txt);
    } catch (err) {
      setErro(err?.response?.data?.error?.message ?? err?.response?.data?.error ?? "Erro ao enviar mensagem.");
      setTexto(txt);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  }

  return (
    <div className="border-t border-white/[0.06] bg-base flex-shrink-0">
      {janelaFechada && (
        <div
          className="px-4 py-2 bg-amber-950 border-b border-amber-800 text-xs text-amber-300 flex items-center gap-2"
          role="status"
        >
          <Clock className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
          Janela de 24 h expirada. Não é possível enviar novas mensagens.
        </div>
      )}

      {erro && (
        <div className="px-4 py-1.5 bg-red-950 border-b border-red-800 text-xs text-red-300" role="alert">
          {typeof erro === "string" ? erro : "Erro ao enviar mensagem."}
        </div>
      )}

      <div className="flex items-end gap-2 px-3 py-2">
        <textarea
          ref={ref}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={janelaFechada || enviando}
          placeholder={
            janelaFechada
              ? "Janela expirada — aguarde o cliente responder"
              : "Escreva uma mensagem… (Enter para enviar, Shift+Enter para nova linha)"
          }
          aria-label="Campo de mensagem"
          rows={1}
          className="flex-1 resize-none bg-surface-2 border border-white/[0.08] rounded-xl px-3 py-2 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors leading-relaxed max-h-32 overflow-y-auto scrollbar-thin scrollbar-track-zinc-800 scrollbar-thumb-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ minHeight: "2.25rem" }}
          onInput={(e) => {
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
          }}
        />

        <button
          type="button"
          onClick={handleEnviar}
          disabled={!podeEnviar}
          aria-label="Enviar mensagem"
          className="mb-0.5 flex-shrink-0 p-2 rounded-xl bg-brand-600 hover:bg-brand-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

/* ──────────────── ChatView ──────────────── */

export default function ChatView({ contaId, conversaId, socket, onVoltar }) {
  const [conversa, setConversa]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [hasMore, setHasMore]     = useState(false);
  const [cursor, setCursor]       = useState(null);
  const [enviando, setEnviando]   = useState(false);

  const listRef     = useRef(null);
  const topo        = useRef(null);
  const primeiraVez = useRef(true);
  const proximoDoFundoRef = useRef(true);
  const ultimoMarcarLidoRef = useRef(0);

  const rolarFundo = useCallback(() => {
    requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, []);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    proximoDoFundoRef.current = dist < 120;
  }, []);

  const marcarLidoThrottled = useCallback((cid) => {
    const agora = Date.now();
    if (agora - ultimoMarcarLidoRef.current < 3000) return;
    ultimoMarcarLidoRef.current = agora;
    apiMarcarLido(contaId, cid).catch(() => {});
  }, [contaId]);

  const carregarMensagens = useCallback(async (cursorParam = undefined) => {
    if (!conversaId) return;
    setCarregando(true);
    try {
      const { mensagens: lista, cursor: prox, hasMore: more } =
        await listMensagens(contaId, conversaId, {
          limit: LIMIT,
          cursor: cursorParam || undefined,
        });
      setHasMore(more);
      setCursor(prox);
      // Backend retorna DESC (mais recente primeiro). UI espera ASC.
      const ordenadaAsc = [...lista].reverse();
      if (cursorParam) {
        setMensagens((prev) => [...ordenadaAsc, ...prev]);
      } else {
        setMensagens(ordenadaAsc);
        primeiraVez.current = true;
      }
    } catch {
      // falha silenciosa
    } finally {
      setCarregando(false);
    }
  }, [contaId, conversaId]);

  /* busca conversa + mensagens ao abrir */
  useEffect(() => {
    if (!conversaId) {
      setConversa(null);
      setMensagens([]);
      return;
    }
    let cancelado = false;
    setLoading(true);
    setMensagens([]);
    setHasMore(false);
    setCursor(null);

    getConversa(contaId, conversaId)
      .then((dados) => { if (!cancelado) setConversa(dados); })
      .catch(() => { if (!cancelado) setConversa(null); })
      .finally(() => { if (!cancelado) setLoading(false); });

    carregarMensagens();
    ultimoMarcarLidoRef.current = 0;
    marcarLidoThrottled(conversaId);
    proximoDoFundoRef.current = true;

    return () => { cancelado = true; };
  }, [contaId, conversaId, carregarMensagens, marcarLidoThrottled]);

  /* scroll para o fundo na primeira carga */
  useEffect(() => {
    if (mensagens.length > 0 && primeiraVez.current) {
      primeiraVez.current = false;
      rolarFundo();
    }
  }, [mensagens, rolarFundo]);

  /* IntersectionObserver — carregar mais ao rolar pra cima */
  useEffect(() => {
    if (!hasMore || !topo.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !carregando && mensagens.length > 0 && cursor) {
          carregarMensagens(cursor);
        }
      },
      { root: listRef.current, threshold: 0.1 }
    );
    obs.observe(topo.current);
    return () => obs.disconnect();
  }, [hasMore, carregando, mensagens, cursor, carregarMensagens]);

  /* Socket.IO — real-time */
  useEffect(() => {
    if (!socket || !conversaId) return;

    socket.emit("join-conversa-ig", conversaId);

    function handleMensagemNew(payload) {
      if (!payload || payload.conversa_id !== conversaId) return;
      const nova = payload.mensagem ?? payload;

      setMensagens((prev) => {
        if (prev.some((m) => m.id === nova.id)) return prev;
        // Substitui mensagem otimista local pelo eco outbound real.
        if (nova.direcao === "out") {
          const textoNova = extrairTexto(nova.conteudo);
          const idx = prev.findIndex(
            (m) => m._otimista && m.direcao === "out" && extrairTexto(m.conteudo) === textoNova
          );
          if (idx !== -1) {
            const copia = [...prev];
            copia[idx] = nova;
            return copia;
          }
        }
        return [...prev, nova];
      });

      if (nova.direcao === "in") {
        setConversa((prev) =>
          prev
            ? { ...prev, janela_expira_em: new Date(Date.now() + 24 * 3600 * 1000).toISOString() }
            : prev
        );
        marcarLidoThrottled(conversaId);
      }

      if (proximoDoFundoRef.current) rolarFundo();
    }

    function handleReconnect() {
      socket.emit("join-conversa-ig", conversaId);
      carregarMensagens();
    }

    socket.on("connect",      handleReconnect);
    socket.on("mensagem:new", handleMensagemNew);

    return () => {
      socket.off("connect",      handleReconnect);
      socket.off("mensagem:new", handleMensagemNew);
      socket.emit("leave-conversa-ig", conversaId);
    };
  }, [socket, conversaId, rolarFundo, marcarLidoThrottled, carregarMensagens]);

  /* envio de texto */
  async function handleEnviar(texto) {
    setEnviando(true);
    // Mensagem otimista — o eco real chega depois pelo socket e dedupa.
    const otimista = {
      id: `temp-${Date.now()}`,
      direcao: "out",
      tipo: "text",
      conteudo: { text: texto },
      data_criacao: new Date().toISOString(),
      _otimista: true,
    };
    setMensagens((prev) => [...prev, otimista]);
    proximoDoFundoRef.current = true;
    rolarFundo();
    try {
      const real = await enviarMensagem(contaId, conversaId, texto);
      // Se o backend devolver a mensagem real, troca a otimista.
      if (real && (real.id || real.conteudo)) {
        setMensagens((prev) => {
          if (prev.some((m) => m.id === real.id)) {
            return prev.filter((m) => !m._otimista || m.id !== otimista.id);
          }
          return prev.map((m) => (m.id === otimista.id ? { ...real, _otimista: false } : m));
        });
      }
    } catch (err) {
      // remove a otimista e propaga pro ChatInput exibir o erro
      setMensagens((prev) => prev.filter((m) => m.id !== otimista.id));
      throw err;
    } finally {
      setEnviando(false);
    }
  }

  /* placeholder — nenhuma conversa */
  if (!conversaId) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-surface-1/40 text-center px-6">
        <MessageSquare className="w-12 h-12 text-ink-5 mb-4" aria-hidden="true" />
        <p className="text-ink-4 text-sm font-medium">Selecione uma conversa</p>
        <p className="text-ink-5 text-xs mt-1">
          Escolha uma conversa na lista para visualizar o histórico
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-surface-1/40">
        <Loader2 className="w-6 h-6 animate-spin text-ink-5" aria-label="Carregando conversa" />
      </div>
    );
  }

  const nome = conversa?.cliente_nome || conversa?.username || "Sem nome";
  const user = conversa?.username ? `@${conversa.username}` : "";
  const janelaOk = conversa?.janela_expira_em
    ? new Date(conversa.janela_expira_em) > new Date()
    : false;

  return (
    <div className="flex flex-col h-full bg-surface-1">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06] bg-base flex-shrink-0">
        {onVoltar && (
          <button
            type="button"
            onClick={onVoltar}
            aria-label="Voltar para lista de conversas"
            className="flex md:hidden text-ink-3 hover:text-ink-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded p-1 -ml-1 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}

        <div
          className="w-8 h-8 rounded-full bg-brand-800 text-brand-200 flex items-center justify-center text-xs font-bold flex-shrink-0 select-none"
          aria-hidden="true"
          title={nome}
        >
          {(nome[0] || "?").toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink-1 truncate leading-tight">{nome}</p>
          {user && <p className="text-xs text-ink-4 truncate">{user}</p>}
        </div>

        <div className="flex-shrink-0">
          <JanelaIndicator janelaExpiraEm={conversa?.janela_expira_em} />
        </div>
      </div>

      {/* Mensagens */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-zinc-900 scrollbar-thumb-zinc-700 py-2"
        aria-label="Histórico de mensagens"
        aria-live="polite"
      >
        <div ref={topo} className="h-1" aria-hidden="true" />

        {carregando && mensagens.length > 0 && (
          <div className="flex justify-center py-2">
            <Loader2 className="w-4 h-4 animate-spin text-ink-5" aria-label="Carregando mensagens anteriores" />
          </div>
        )}

        {!carregando && mensagens.length === 0 && (
          <div className="flex flex-col items-center justify-center h-32 text-center px-4">
            <p className="text-xs text-ink-5 italic">Nenhuma mensagem ainda.</p>
          </div>
        )}

        {(() => {
          let ultimoDia = null;
          const nodes = [];
          for (const msg of mensagens) {
            const ts = timestampMsg(msg);
            const dia = chaveDia(ts);
            if (dia && dia !== ultimoDia) {
              nodes.push(<DiaSeparador key={`dia-${dia}-${msg.id ?? ""}`} iso={ts} />);
              ultimoDia = dia;
            }
            nodes.push(
              <MessageBubble key={msg.id} mensagem={msg} eu={msg.direcao === "out"} />
            );
          }
          return nodes;
        })()}
      </div>

      {/* Input */}
      <ChatInput janelaAberta={janelaOk} enviando={enviando} onEnviar={handleEnviar} />
    </div>
  );
}
